import type { ColorSpec } from './types';

/**
 * Orange color spec — uses the `blaze-orange` Tailwind palette.
 *
 * Reserved for "stuck until a PERSON acts": the sequence People list's needs-you pill, matching the
 * blaze-orange the per-contact sequence graph already draws for the same state (`sequenceStepStatus`'s
 * `needsYou` tone). Deliberately distinct from `yellow` (blocked but auto-retrying) and `red` (errored) —
 * conflating them is exactly what made a blocked contact indistinguishable from a healthy one.
 */
export const orange: ColorSpec = {
  filled: {
    border: 'border-blaze-orange-700 dark:border-blaze-orange-500',
    bg: 'bg-blaze-orange-600 text-white dark:bg-blaze-orange-700',
    bgActive: 'bg-blaze-orange-800 text-white',
    hover: 'hover:bg-blaze-orange-700 hover:text-white active:bg-blaze-orange-800 active:text-white',
    hoverDark:
      'dark:hover:bg-blaze-orange-800 dark:hover:text-white dark:active:bg-blaze-orange-900 dark:active:text-white',
  },
  outline: {
    border: 'border-blaze-orange-400 dark:border-blaze-orange-500',
    bg: 'bg-blaze-orange-50 dark:bg-blaze-orange-950 text-blaze-orange-700 dark:text-blaze-orange-200',
    bgActive: 'bg-blaze-orange-600 dark:bg-blaze-orange-500 text-white',
    hover: 'hover:bg-blaze-orange-100 hover:text-blaze-orange-800 active:bg-blaze-orange-200 active:text-blaze-orange-800',
    hoverDark:
      'dark:hover:bg-blaze-orange-900 dark:hover:text-blaze-orange-100 dark:active:bg-blaze-orange-800 dark:active:text-blaze-orange-100',
    hoverWhenActive: 'hover:bg-blaze-orange-700 dark:hover:bg-blaze-orange-600',
  },
  soft: {
    border: 'border-blaze-orange-100 dark:border-blaze-orange-900',
    bg: 'bg-blaze-orange-50 dark:bg-blaze-orange-950 text-blaze-orange-700 dark:text-blaze-orange-300 font-normal',
    bgActive: 'bg-blaze-orange-600 dark:bg-blaze-orange-500 text-white',
    hover: 'hover:bg-blaze-orange-100 hover:text-blaze-orange-900 active:bg-blaze-orange-200 active:text-blaze-orange-950',
    hoverDark:
      'dark:hover:bg-blaze-orange-900 dark:hover:text-blaze-orange-100 dark:active:bg-blaze-orange-800 dark:active:text-blaze-orange-100',
    hoverWhenActive: 'hover:bg-blaze-orange-700 dark:hover:bg-blaze-orange-600',
  },
  textFilled: {
    text: 'text-blaze-orange-600 dark:text-blaze-orange-400',
    activeBg: 'bg-blaze-orange-600 text-white dark:bg-blaze-orange-700',
    hover: 'hover:bg-blaze-orange-600 hover:text-white active:bg-blaze-orange-700',
    hoverDark: 'dark:hover:bg-blaze-orange-700 dark:hover:text-white dark:active:bg-blaze-orange-800',
  },
  textSoft: {
    text: 'text-blaze-orange-700 dark:text-blaze-orange-300 font-normal',
    activeBg: 'bg-blaze-orange-600 text-white dark:bg-blaze-orange-500',
    hover: 'hover:bg-blaze-orange-100 hover:text-blaze-orange-900 active:bg-blaze-orange-200',
    hoverDark: 'dark:hover:bg-blaze-orange-900 dark:hover:text-blaze-orange-100 dark:active:bg-blaze-orange-800',
  },
  tooltip: 'bg-blaze-orange-600 dark:bg-blaze-orange-700 text-white',
  badge: {
    filled: 'bg-blaze-orange-600 dark:bg-blaze-orange-700 text-white',
    soft: 'bg-blaze-orange-400 dark:bg-blaze-orange-500 text-white',
  },
  softIcon: 'text-blaze-orange-700 dark:text-blaze-orange-200',
  underline: {
    filled:
      '[&_u]:text-blaze-orange-200 dark:[&_u]:text-blaze-orange-300 [&_u]:decoration-blaze-orange-300 dark:[&_u]:decoration-blaze-orange-400',
    outline:
      '[&_u]:text-blaze-orange-800 dark:[&_u]:text-blaze-orange-300 [&_u]:decoration-blaze-orange-600 dark:[&_u]:decoration-blaze-orange-400',
    soft: '[&_u]:text-blaze-orange-800 dark:[&_u]:text-blaze-orange-300 [&_u]:decoration-blaze-orange-300 dark:[&_u]:decoration-blaze-orange-400',
  },
  input: {
    filled: {
      border: 'border-blaze-orange-700 dark:border-blaze-orange-500',
      bg: 'bg-blaze-orange-600 dark:bg-blaze-orange-700',
      focusBg: 'focus-within:bg-blaze-orange-700 dark:focus-within:bg-blaze-orange-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-blaze-orange-700 dark:focus-within:outline-blaze-orange-500',
      hoverActive:
        'hover:bg-blaze-orange-700 hover:text-white active:bg-blaze-orange-800 active:text-white dark:hover:bg-blaze-orange-800 dark:active:bg-blaze-orange-900',
      readOnlyBg: 'bg-blaze-orange-500 dark:bg-blaze-orange-800',
      text: 'text-white',
    },
    outline: {
      border: 'border-blaze-orange-400 dark:border-blaze-orange-500',
      bg: 'bg-blaze-orange-50 dark:bg-blaze-orange-950',
      focusBg: 'focus-within:bg-blaze-orange-100 dark:focus-within:bg-blaze-orange-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-blaze-orange-400 dark:focus-within:outline-blaze-orange-500',
      hoverActive:
        'hover:bg-blaze-orange-100 hover:text-blaze-orange-800 active:bg-blaze-orange-200 active:text-blaze-orange-800 dark:hover:bg-blaze-orange-900 dark:hover:text-blaze-orange-100 dark:active:bg-blaze-orange-800 dark:active:text-blaze-orange-100',
      readOnlyBg: 'bg-blaze-orange-50 dark:bg-blaze-orange-900',
      text: 'text-blaze-orange-700 dark:text-blaze-orange-200',
    },
    soft: {
      border: 'border-blaze-orange-100 dark:border-blaze-orange-900',
      bg: 'bg-blaze-orange-50 dark:bg-blaze-orange-950',
      focusBg: 'focus-within:bg-blaze-orange-100 dark:focus-within:bg-blaze-orange-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-blaze-orange-100 dark:focus-within:outline-blaze-orange-900',
      hoverActive:
        'hover:bg-blaze-orange-100 hover:text-blaze-orange-900 active:bg-blaze-orange-200 active:text-blaze-orange-950 dark:hover:bg-blaze-orange-900 dark:hover:text-blaze-orange-100 dark:active:bg-blaze-orange-800 dark:active:text-blaze-orange-100',
      readOnlyBg: 'bg-blaze-orange-50 dark:bg-blaze-orange-900',
      text: 'text-blaze-orange-700 dark:text-blaze-orange-300',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-blaze-orange-300 dark:text-blaze-orange-600',
    soft: 'text-blaze-orange-300 dark:text-blaze-orange-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-blaze-orange-300 dark:placeholder:text-blaze-orange-600',
    soft: 'placeholder:text-blaze-orange-300 dark:placeholder:text-blaze-orange-600',
  },
  group: {
    filled: 'border-blaze-orange-300 dark:border-blaze-orange-700 bg-blaze-orange-50 dark:bg-blaze-orange-950',
    outline: 'border-blaze-orange-400 dark:border-blaze-orange-500 bg-blaze-orange-50 dark:bg-blaze-orange-950',
    soft: 'border-blaze-orange-200 dark:border-blaze-orange-800 bg-blaze-orange-100 dark:bg-blaze-orange-900',
  },
  groupDivider: {
    filled: 'bg-blaze-orange-300 dark:bg-blaze-orange-700',
    outline: 'bg-blaze-orange-300 dark:bg-blaze-orange-600',
    soft: 'bg-blaze-orange-200 dark:bg-blaze-orange-700',
  },
  segment: {
    filled: 'border-blaze-orange-300 dark:border-blaze-orange-700 bg-blaze-orange-50 dark:bg-blaze-orange-950',
    outline: 'border-blaze-orange-400 dark:border-blaze-orange-500 bg-blaze-orange-50 dark:bg-blaze-orange-950',
  },
  segmentDivider: {
    filled: 'bg-blaze-orange-300 dark:bg-blaze-orange-700',
    outline: 'bg-blaze-orange-300 dark:bg-blaze-orange-600',
  },
  popoverHighlight: 'bg-blaze-orange-600 text-white',
  popoverText: 'text-blaze-orange-700 dark:text-blaze-orange-200',
  popoverBorder: 'border-blaze-orange-200 dark:border-blaze-orange-700',
};
